import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Tooltip, message } from 'antd';
import { QuestionCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import s from './s.module.less';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import {
    TKlarityServiceServerTableItem,
    TKlarityServiceUiTableItem,
    Meta,
    EContactType,
    KlarityServiceTypeInfo,
    ServerMeta,
    ELicenseType,
    KlarityService,
    ServerKlarityService,
} from 'types/common';
import { SettingEnum } from 'types/enumerationData';
import { getSpecialtiesConditions } from 'api/applicationForm';
import ServiceTable from 'components/ApplicationFormComponents/KlarityServices/components/ServiceTable';
import { getProviderSpecialtyDetail, getSpecialityTemplateDetail } from 'api/klarityService';
import { LA_WILD_FIRE_SPECIALTY_NAME } from 'constants/common';
import { ProviderLicenseFromDataWithoutState, UniprofileFormData } from 'types/provider';
import { FormLicense } from 'types/form';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';

interface Step1ServiceListProps {
    onEdit: () => void;
    setIsEdit: (isEdit: boolean) => void;
    onAddNewService: () => void;
    allStates?: SettingEnum[] | Meta[];
    isKiwiUser?: boolean;
    provider: UniprofileFormData;
    licenses?: ProviderLicenseFromDataWithoutState;
    setEditSpecialityValue: (specialties: ServerKlarityService[]) => void;
    setEditingData: (data: KlarityService[]) => void;
    tableData?: TKlarityServiceServerTableItem[];
    tableDataLoading?: boolean;
    refetchTableData: () => void;
}

type TSpecialtyCondition = {
    conditions: string;
    providerId: number;
    specialtyId: number;
    specialtyName: string;
    id: number;
};

const Step1ServiceList: React.FC<Step1ServiceListProps> = ({
    tableData,
    tableDataLoading,
    onAddNewService,
    onEdit,
    setIsEdit,
    allStates,
    isKiwiUser,
    provider,
    licenses,
    setEditSpecialityValue,
    setEditingData,
    refetchTableData,
}) => {
    const [specialtiesConditions, setSpecialtiesConditions] = useState<TSpecialtyCondition[]>([]);
    const [fetchSpecialtiesConditionsLoading, setFetchSpecialtiesConditionsLoading] = useState(false);
    const [fetchingEdit, setFetchingEdit] = useState(false);
    // const [editSpecialityValue, setEditSpecialityValue] = useState<TKlarityServiceUiTableItem[]>([]);
    const [licenseStates, setLicenseStates] = useState<ServerMeta[]>([]);
    const providerId = provider?.id;
    const navigate = useNavigate();

    // 获取specialty conditions数据
    const fetchSpecialtiesConditions = async () => {
        setFetchSpecialtiesConditionsLoading(true);
        try {
            const result = await getSpecialtiesConditions();
            if (result && !result.error) {
                setSpecialtiesConditions(result.data?.data || []);
            } else {
                message.error(result?.error);
            }
        } catch (error) {
            // console.error(error);
        } finally {
            setFetchSpecialtiesConditionsLoading(false);
        }
    };

    useEffect(() => {
        fetchSpecialtiesConditions();
    }, []);

    const getTableUseData = useCallback(
        (serverData?: TKlarityServiceServerTableItem[]): TKlarityServiceUiTableItem[] => {
            const ret: TKlarityServiceUiTableItem[] = [];
            const allSpeIds: number[] = [];
            if (!allStates || allStates.length === 0) {
                return [];
            }
            serverData?.forEach((item: TKlarityServiceServerTableItem) => {
                if (!allSpeIds.includes(item.serviceSpecialtyId)) {
                    allSpeIds.push(item.serviceSpecialtyId);
                }
            });
            allSpeIds.forEach((speId: number) => {
                const speData: TKlarityServiceServerTableItem[] =
                    serverData?.filter((item: TKlarityServiceServerTableItem) => item.serviceSpecialtyId === speId) ||
                    [];
                speData.sort((a, b) => {
                    return a.state && b.state ? a.state.localeCompare(b.state) : 0;
                });
                if (speData.length > 0) {
                    const firstData = speData[0];
                    const add: TKlarityServiceUiTableItem = {
                        serviceSpecialtyName: firstData.serviceSpecialtyName,
                        serviceSpecialtyId: firstData.serviceSpecialtyId,
                        serviceTypeInfo: [],
                    };
                    speData.forEach((item: TKlarityServiceServerTableItem, inx) => {
                        const state = (allStates as SettingEnum[])?.find(
                            (st: SettingEnum) => st.dictKey === item.state || st.content === item.state,
                        );
                        const contactType: EContactType[] = [];
                        if (item.inPerson) {
                            contactType.push(EContactType.IN_PERSON);
                        }
                        if (item.teleHealth) {
                            contactType.push(EContactType.TELE_HEALTH);
                        }
                        //is save spe
                        const addData: KlarityServiceTypeInfo = {
                            state: state as ServerMeta,
                            serviceId: item.serviceId,
                            serviceType: item.serviceType,
                            customizedServiceName: item.customizedServiceName || '',
                            earning: item.earning,
                            patientCost: item.patientCost,
                            contactType,
                            groupAndLimitationId: item.groupAndLimitationId,
                            qtyPatientGroup: item.qtyPatientGroup,
                            qtyLimitation: item.qtyLimitation,
                            accpet: undefined,
                            limitations: undefined,
                            rowId: inx,
                            allPatientGroup: item.allPatientGroup,
                            avaPriceInsight: item.avaPriceInsight,
                            klarityTemplate: item.klarityTemplate,
                            intakeFormFlag: item.intakeFormFlag,
                            allowControlledMedRefill: item.allowControlledMedRefill,
                        };
                        if (isKiwiUser) {
                            addData.insurances = item.insurances;
                            addData.priorAuthorizationSupport = item.priorAuthorizationSupport;
                            addData.superBillSupport = item.superBillSupport;
                        }
                        add.serviceTypeInfo!.push(addData);
                    });
                    ret.push(add);
                }
            });
            return ret.filter((item) => item.serviceSpecialtyName !== LA_WILD_FIRE_SPECIALTY_NAME);
        },
        [allStates, isKiwiUser],
    );

    const fetchEditDetailAndShowModal = useCallback(
        async (currentEditService: TKlarityServiceUiTableItem) => {
            setFetchingEdit(true);
            setIsEdit(true);
            // if (isOps && providerId) {
            //     templateDetailResult = await getOpsSpecialityTemplateDetail(currentEditService.serviceSpecialtyId);
            // } else {
            //     templateDetailResult = await getSpecialityTemplateDetail(currentEditService.serviceSpecialtyId);
            // }
            const templateDetailResult: any = await getSpecialityTemplateDetail(currentEditService.serviceSpecialtyId);
            if (!templateDetailResult.error) {
                const configItem = templateDetailResult?.data?.data;
                setEditingData([configItem]);
            }
            const detailDataResult: any = await getProviderSpecialtyDetail(currentEditService.serviceSpecialtyId);
            // if (isOps && providerId) {
            //     detailDataResult = await getOpsProviderSpecialtyDetail(currentEditService.serviceSpecialtyId, providerId);
            // } else {
            //     detailDataResult = await getProviderSpecialtyDetail(currentEditService.serviceSpecialtyId);
            // }
            if (!detailDataResult.error) {
                setEditSpecialityValue(detailDataResult?.data?.data);
            }
            setFetchingEdit(false);
            setTimeout(() => {
                onEdit?.();
            }, 300);
        },
        [setIsEdit],
    );

    React.useEffect(() => {
        if (!licenses) {
            return;
        }
        //const deaLicense = licenses[ELicenseType.DEA];
        const practiceLicense = licenses[ELicenseType.PRACTICE];
        const stateKeys: string[] = [];
        // deaLicense?.forEach((license:FormLicense) => {
        //     if (!stateKeys.includes(license.state)) {
        //         stateKeys.push(license.state);
        //     }
        // });
        practiceLicense?.forEach((license: FormLicense) => {
            if (!stateKeys.includes(license.state)) {
                stateKeys.push(license.state);
            }
        });

        const _licenseStates = (allStates as ServerMeta[])?.filter((state: ServerMeta) =>
            stateKeys.includes(state.dictKey),
        );

        setLicenseStates(_licenseStates || []);
    }, [allStates, licenses]);

    const handleAddNewService = useCallback(() => {
        // 跳转到步骤2 - Specialty选择页面
        setIsEdit(false);
        onAddNewService();
    }, [setIsEdit, onAddNewService]);

    return (
        <div className={s.wrap}>
            {/* 顶部内容区域 */}
            <div className={s.header}>
                <div className={s.titleSection}>
                    <h1 className={s.title}>Review your service setup</h1>
                    <p className={s.subtitle}>
                        Please confirm your service details before moving forward. You&apos;ll be able to make edits
                        later if needed.
                    </p>
                </div>

                <Button
                    type="primary"
                    className={s.addServiceBtn}
                    icon={<PlusOutlined />}
                    onClick={handleAddNewService}
                >
                    Add new service
                </Button>
            </div>

            {/* 服务列表内容区域 */}
            <div className={s.content}>
                <ServiceTable
                    hideHeader
                    isServiceSetting
                    isKiwiUser
                    onClickAdd={() => {}}
                    onClickEdit={(currentEditService: TKlarityServiceUiTableItem) => {
                        fetchEditDetailAndShowModal(currentEditService);
                    }}
                    loading={tableDataLoading}
                    data={getTableUseData(tableData || [])}
                    providerId={providerId}
                    // showSyncButton={showSyncButton}
                    // onClickSync={onClickSync}
                    licenseStates={licenseStates}
                    showEditButtons
                    hideFooter
                    emptyComponent="Service list is empty"
                    refetchTableData={refetchTableData}
                />
            </div>
            <div className={s.footer}>
                <Button
                    onClick={() => {
                        navigate(`${PATH.DASHBOARD}`);
                    }}
                    className={s.backBtn}
                >
                    Exit
                </Button>
            </div>
        </div>
    );
};

export default Step1ServiceList;
